/**
 * `ColumnDef` lists for the service tables. `serviceColumns` covers the main /
 * waiting-confirm / device-liquidation feeds; `pocServiceColumns` swaps the
 * role/direction/cycle columns for the POC response/retention columns. Mirrors
 * the legacy `createServiceColumns` / `createPOCServiceColumns` 1-1 on the shared
 * cell renderers.
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import type { ServiceRow } from '@/api/services';
import {
  dateCell,
  dateRangeCell,
  directionCell,
  listCell,
  roleCell,
  serviceCustomerCell,
  serviceIdCell,
  serviceNameCell,
  serviceStatusCell,
  textCell,
} from './service-cell-renderers';

export type ServiceCol = ColumnDef<ServiceRow, unknown>;

const rightHeader = (label: string) => () => (
  <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>
);

/* ── Main service columns (all / waiting-confirm / device-liquidation) ───── */

export const serviceColumns: ServiceCol[] = [
  { id: 'service_id', header: 'ID', cell: ({ row }) => serviceIdCell(row.original) },
  { id: 'product_group_name', header: 'Product group', cell: ({ row }) => textCell(row.original.product_group_name) },
  { id: 'service_name', header: 'Service', cell: ({ row }) => serviceNameCell(row.original.service_name) },
  { id: 'name', header: 'Entity name', cell: ({ row }) => textCell(row.original.name) },
  { id: 'cs_followers', header: 'CS followers', cell: ({ row }) => listCell(row.original.cs_followers) },
  { id: 'customer', header: 'Customer', cell: ({ row }) => serviceCustomerCell(row.original) },
  { id: 'service_status', header: 'Status', cell: ({ row }) => serviceStatusCell(row.original.service_status) },
  { id: 'contract_role', header: 'Role', cell: ({ row }) => roleCell(row.original.contract_role) },
  { id: 'contract_direction', header: 'Direction', cell: ({ row }) => directionCell(row.original.contract_direction) },
  {
    id: 'service_active_cycling',
    header: 'Active cycle',
    cell: ({ row }) => dateRangeCell(row.original.current_startdate, row.original.current_enddate),
  },
  {
    id: 'service_renew_cycling',
    header: 'Renew cycle',
    cell: ({ row }) =>
      row.original.service_status === 'cancelled'
        ? null
        : dateRangeCell(row.original.continue_startdate, row.original.continue_enddate),
  },
  { id: 'service_start_date', header: rightHeader('Start'), cell: ({ row }) => dateCell(row.original.service_start_date) },
  { id: 'service_stopped_date', header: rightHeader('Stopped'), cell: ({ row }) => dateCell(row.original.service_stopped_date) },
  { id: 'created_at', header: rightHeader('Created'), cell: ({ row }) => dateCell(row.original.created_at) },
];

/* ── POC service columns ─────────────────────────────────────────────────── */

export const pocServiceColumns: ServiceCol[] = [
  { id: 'service_id', header: 'ID', cell: ({ row }) => serviceIdCell(row.original) },
  { id: 'product_group_name', header: 'Product group', cell: ({ row }) => serviceNameCell(row.original.product_group_name) },
  { id: 'service_name', header: 'Service', cell: ({ row }) => textCell(row.original.service_name) },
  { id: 'customer', header: 'Customer', cell: ({ row }) => serviceCustomerCell(row.original) },
  { id: 'cs_followers', header: 'CS followers', cell: ({ row }) => listCell(row.original.cs_followers) },
  { id: 'service_status', header: 'Status', cell: ({ row }) => serviceStatusCell(row.original.service_status) },
  { id: 'service_response_poc', header: 'POC response', cell: ({ row }) => listCell(row.original.service_response_poc) },
  { id: 'service_retention_poc', header: 'POC retention', cell: ({ row }) => listCell(row.original.service_retention_poc) },
  { id: 'service_start_date', header: rightHeader('Start'), cell: ({ row }) => dateCell(row.original.service_start_date) },
  { id: 'service_stopped_date', header: rightHeader('Stopped'), cell: ({ row }) => dateCell(row.original.service_stopped_date) },
  { id: 'created_at', header: rightHeader('Created'), cell: ({ row }) => dateCell(row.original.created_at) },
];
